import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { cartAdd, dispatchCartAdd } from '@theme/r-cart-actions';
import { mediaQueryLarge, onAnimationEnd, yieldToMainThread } from '@theme/utilities';

// How long the added checkmark stays readable before the button resets
// (mirrored in product-form.js)
const ADDED_STATE_RESET_DELAY = 800;

// Floor on how long the in-flight state is shown. A fast cart response would
// otherwise flash "Adding" for a frame before "Added" replaces it, which reads
// as a glitch rather than as feedback. The cart event still fires when the
// network answers; only the button waits.
const MIN_BUSY_DISPLAY = 400;

// How long a failed add stays red on the pill before it returns to rest.
const ERROR_STATE_RESET_DELAY = 2400;

/**
 * Inline quick add for a product card.
 *
 * This used to open a modal: `handleClick` fetched the *entire product page*,
 * pulled `[data-product-grid-content]` out of it, cached the clone, and morphed
 * it into a single global `<dialog>` shared by every card on the page. That
 * bought a full PDP inside a lightbox — a media gallery, a variant picker, a
 * `product-form-component` — to answer one question: which size?
 *
 * Now the pill expands in place into a list of option values, and a row click
 * adds that variant. No fetch, no dialog, no `<form>`. Which mode a card is in
 * is decided server-side in `snippets/quick-add.liquid` and read off
 * `data-mode`; see that file for the rule.
 *
 * Adds go through `cartAdd()` rather than `ProductFormComponent`, because that
 * component requires an enclosing `<form>` (`product-form.js` throws
 * 'Product form element missing' without one) and exists to serve the PDP's
 * variant-update plumbing, none of which applies here.
 *
 * @typedef {object} QuickAddRefs
 * @property {HTMLElement} trigger - The pill.
 * @property {HTMLElement} shell - The capsule that owns the chrome.
 * @property {HTMLElement} [panel] - The variant list. Absent outside `choose` mode.
 * @property {HTMLElement} [status] - Live region.
 *
 * @extends Component<QuickAddRefs>
 */
export class QuickAddComponent extends Component {
  requiredRefs = ['trigger'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #addedTimeout;

  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointer, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    mediaQueryLarge.addEventListener('change', this.#collapse, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    clearTimeout(this.#addedTimeout);
    clearTimeout(this.#errorTimeout);
  }

  /** @returns {string} `add`, `choose` or `link`. */
  get mode() {
    return this.dataset.mode ?? 'link';
  }

  /** @returns {boolean} */
  get expanded() {
    return this.dataset.state === 'expanded';
  }

  /** @returns {boolean} An add is in flight (or being held for `MIN_BUSY_DISPLAY`). */
  get busy() {
    return this.dataset.busy === 'true';
  }

  /**
   * The pill.
   *
   * `add` adds the only variant. `choose` toggles the panel. `link` navigates —
   * the trigger is a real `<a>` so a plain click never reaches here, but
   * `product-hotspot.js` calls this method programmatically to open quick add
   * from its dialog, and that path has no anchor to follow.
   *
   * @param {Event} [event]
   */
  handleClick = async (event) => {
    event?.preventDefault();

    switch (this.mode) {
      case 'add': {
        const variantId = this.dataset.variantId;
        if (!variantId) return;
        await this.#add(this.refs.trigger, variantId, Number(this.dataset.quantity) || 1, this.#productTitle);
        return;
      }
      case 'choose':
        this.expanded ? this.#collapse() : this.#expand();
        return;
      default:
        if (this.dataset.productUrl) window.location.href = this.dataset.productUrl;
    }
  };

  /**
   * A row in the expanded panel.
   * @param {Event} event
   */
  handleVariantAdd = async (event) => {
    event.preventDefault();

    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.quick-add__variant') : null
    );

    if (!button) return;
    if (button.dataset.unavailable === 'true' || this.busy) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const label = button.dataset.variantTitle
      ? `${this.#productTitle} — ${button.dataset.variantTitle}`
      : this.#productTitle;

    await this.#add(button, variantId, Number(button.dataset.quantity) || 1, label);
  };

  /** @returns {string} */
  get #productTitle() {
    return this.dataset.productTitle ?? '';
  }

  /**
   * Adds a line and reports the outcome on the button that was pressed.
   *
   * Feedback starts on the same tick as the press: `data-busy` on the host
   * swaps the pill's label for "Adding" and starts the sweep, the pressed
   * control takes `aria-busy`, and the live region announces — all before the
   * request is sent. The whole control is busy, not just the pressed row, so a
   * second row cannot start a second add while one is in flight.
   *
   * The busy state uses `aria-disabled`, not `disabled`, so the button keeps its
   * place in the focus order while the request is in flight — the same choice
   * `r-cart-bumpers.js` documents. On failure the row stays exactly where it is:
   * the shopper wanted that variant, and removing it takes away the only thing
   * they can retry.
   *
   * @param {HTMLElement} button - The pressed control.
   * @param {string} variantId
   * @param {number} quantity
   * @param {string} label - Human name, for announcements.
   */
  async #add(button, variantId, quantity, label) {
    if (this.busy) return;

    const { trigger } = this.refs;

    clearTimeout(this.#addedTimeout);
    clearTimeout(this.#errorTimeout);
    delete trigger.dataset.added;
    delete trigger.dataset.state;

    this.dataset.busy = 'true';
    button.dataset.state = 'busy';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    this.#announce(this.#translate('adding', label));

    const startedAt = performance.now();
    const result = await cartAdd({ id: variantId, quantity });
    await this.#holdBusy(startedAt);

    delete this.dataset.busy;
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');

    if (!result.ok) {
      button.dataset.state = 'error';
      this.#announce(this.#translate('error', label, result.error));

      // A row keeps its error until the panel closes; the pill returns to rest
      // on its own so the shopper can try again from the same place.
      if (button === trigger) {
        this.#errorTimeout = setTimeout(() => {
          delete trigger.dataset.state;
        }, ERROR_STATE_RESET_DELAY);
      }
      return;
    }

    button.dataset.state = 'added';
    this.#announce(this.#translate('added', label));

    this.#playAddedAnimation();

    dispatchCartAdd(await result.cart, result.sourceSectionId, {
      productId: this.dataset.productId,
      variantId,
    }, this);
  }

  /**
   * Waits out the remainder of `MIN_BUSY_DISPLAY`, if any.
   * @param {number} startedAt - `performance.now()` at press time.
   * @returns {Promise<void>}
   */
  #holdBusy(startedAt) {
    const remaining = MIN_BUSY_DISPLAY - (performance.now() - startedAt);
    if (remaining <= 0) return Promise.resolve();

    return new Promise((resolve) => setTimeout(resolve, remaining));
  }

  /* ↗ .cursor/references/code-notes/quick-add.js.md#§1 */
  async #playAddedAnimation() {
    const { trigger } = this.refs;

    trigger.dataset.added = 'true';

    await yieldToMainThread();
    await onAnimationEnd(trigger, () => {});

    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => {
      delete trigger.dataset.added;
      if (trigger.dataset.state === 'added') delete trigger.dataset.state;
    }, ADDED_STATE_RESET_DELAY);
  }

  #expand() {
    const { panel, trigger } = this.refs;
    if (!panel) return;

    this.dataset.state = 'expanded';
    trigger.setAttribute('aria-expanded', 'true');
    panel.removeAttribute('inert');
  }

  /* ↗ .cursor/references/code-notes/quick-add.js.md#§2 */
  #collapse = () => {
    const { panel, trigger } = this.refs;
    if (!panel || !this.expanded) return;

    const hadFocus = panel.contains(document.activeElement);

    this.dataset.state = 'collapsed';
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('inert', '');

    if (hadFocus) trigger.focus();

    for (const button of panel.querySelectorAll('.quick-add__variant[data-state]')) {
      if (button instanceof HTMLElement) delete button.dataset.state;
    }
  };

  /** @param {Event} event */
  #handleOutsidePointer = (event) => {
    if (!this.expanded) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#collapse();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.expanded) return;

    event.stopPropagation();
    this.#collapse();
  };

  /* Another surface changed the cart. Leave this control alone while its own
     add is in flight — collapsing would wipe the pressed row's state mid-request. */
  #handleCartUpdate = () => {
    if (this.busy) return;
    this.#collapse();
  };

  /**
   * @param {string} message
   */
  #announce(message) {
    const { status } = this.refs;
    if (!status || !message) return;

    status.textContent = message;
  }

  /**
   * Fills a `__TITLE__` sentinel template rendered by Liquid onto this element.
   *
   * The sentinel exists because a literal `{{ … }}` inside a Liquid output tag
   * is rejected by Shopify's uploader, so the interpolation cannot round-trip
   * as-is. Same convention as `r-cart-bumpers`.
   *
   * @param {'adding' | 'added' | 'error'} kind
   * @param {string} title
   * @param {string | null} [detail] - Server error text, preferred when present.
   * @returns {string}
   */
  #translate(kind, title, detail) {
    if (kind === 'error' && detail) return detail;

    const template = {
      adding: this.dataset.announceAdding,
      added: this.dataset.announceAdded,
      error: this.dataset.announceError,
    }[kind];

    return template ? template.replace('__TITLE__', title) : title;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
